import { $f } from "@shared/hooks/shared";
import { useUser } from "@shared/hooks/user";
import { clsx } from "clsx";
import { PropsWithChildren } from "react";
import { useIntl } from "react-intl";

function truncFraction(num: string | number, digits: number) {
  const factor = Math.pow(10, digits);
  return Math.floor(Number(num) * factor) / factor;
}

const formatOdds = (num: string | number) => truncFraction(num, 2);
const formatMoney = (num: string | number) => $f.formatMoney($f.toMoney(num));

export type TCoupon = {
  uid: string;
  createdAt: string;
  description: string;
  odds: string;
  selectionsNumber: number;
  eventsToGuessNum: number;
  type: "single" | "multiple";
  bet: number;
  win: number;
  currency: string;
  status: "outcome" | "cancelled" | "created" | "cashout";
  outcomeStatus:
    | "win"
    | "lost"
    | "cancelled"
    | "half_win"
    | "half_lost"
    | "draw"
    | null;
  events: Array<{
    name: string;
    sport: string;
    odds: string;
    categoryName: string;
    marketName: string;
    selName: string;
    status: string | null;
    time: string;
  }>;
  bonusAmount?: number;
  totalWin?: number;
  isPayed: boolean;
  typeEx: "single" | "express" | "system";
};

export function Coupon({
  createdAt,
  uid,
  eventsToGuessNum,
  typeEx,
  odds,
  bet,
  win,
  status,
  outcomeStatus,
  events,
  description,
  bonusAmount = 0,
}: TCoupon) {
  const intl = useIntl();
  const isCancelled = status === "cancelled" || outcomeStatus === "cancelled";
  const isWin = outcomeStatus === "win";
  const isHalfWin = outcomeStatus === "half_win";
  const isHalfLost = outcomeStatus === "half_lost";
  const isDraw = outcomeStatus === "draw";
  const isLost = outcomeStatus === "lost";

  const { currency } = useUser();
  const formattedDate = new Date(createdAt).toLocaleDateString();
  const formattedUid = uid?.match(/.{1,4}/g)?.join(" ") ?? uid;
  const hasBonus = bonusAmount > 0;
  const formattedBonus = `${formatMoney(bonusAmount)} ${currency}`;
  const formattedOdds = formatOdds(odds);
  const formattedBet = `${formatMoney(bet)} ${currency}`;
  const formattedWin = `${formatMoney(win)} ${currency}`;

  let BottomRightStatus: React.ReactNode = <></>;

  if (hasBonus && (isWin || isHalfWin || isHalfLost || isDraw)) {
    BottomRightStatus = (
      <div className="br font-light">
        <span>
          {intl.formatMessage({
            id: "boilerplate.components.ModalRoom.BonusBalance",
          })}
        </span>
        : <span className="font-bold">{formattedBonus}</span>
      </div>
    );
  } else if (status === "cashout") {
    BottomRightStatus = (
      <div className="text-[0.875em] br font-bold uppercase">
        {intl.formatMessage({
          id: "boilerplate.components.SportBetsHistoryModal.Cashout",
        })}
      </div>
    );
  } else if (isCancelled) {
    BottomRightStatus = (
      <div className="text-[0.875em] tr font-bold uppercase">
        {intl.formatMessage({
          id: "boilerplate.components.SportBetsHistoryModal.Cancelled",
        })}
      </div>
    );
  }

  return (
    <div className="coupon text-[0.875em] rounded-rounded">
      <div
        className={`px-5 py-2.5 ${
          outcomeStatus === "win" ? "bg-keppel" : "bg-mercury"
        }`}
      >
        <div className="flex-c-sb font-bold uppercase">
          <div className="whitespace-pre">
            {formattedDate} {typeEx === "single" ? typeEx : description}
          </div>
          {eventsToGuessNum > 1 && <div>{formattedOdds}</div>}
        </div>
      </div>
      {events.map((ev, idx) => {
        const isWin = ev.status === "win";
        const isLose = ev.status === "lose";

        return (
          <div
            className={clsx(
              "px-5 py-2.5 gap-2.5 bg-athens-gray flex flex-col overflow-hidden",
              idx > 0 &&
                "border-0 border-dashed border-t-2 border-mercury rounded-t-xl",
              events.length > 1 && idx !== events.length - 1 && "rounded-b-xl",
              `coupon-status_${ev.status}`
            )}
            key={idx}
          >
            <div className="flex-c-sb">
              <div className="font-light">{ev.name}</div>
              <div className="font-bold">{formatOdds(ev.odds)}</div>
            </div>
            <div className="flex-c-sb font-bold">
              <div className="text-theme-color">{ev.sport}</div>
              <div>{ev.categoryName}</div>
            </div>
            <div className="flex-c-sb">
              <div className="whitespace-pre">
                <span className="font-bold">{ev.selName}</span>{" "}
                <span className="font-light">{`(${ev.marketName})`}</span>
              </div>
              <div className="size-5.5">
                {isWin ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                    viewBox="0 0 22 23"
                  >
                    <path
                      className="fill-keppel"
                      fillRule="evenodd"
                      d="M11 22.904c6.075 0 11-4.925 11-11s-4.925-11-11-11-11 4.925-11 11 4.925 11 11 11Zm-.869-4.759 8.108-9.266-2.258-1.975-5.893 6.734L6.22 9.383 4 11.4l6.131 6.744Z"
                    />
                  </svg>
                ) : (
                  isLose && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 22 23"
                    >
                      <path
                        className="fill-radical-red"
                        fillRule="evenodd"
                        d="M11 22.904c6.075 0 11-4.925 11-11s-4.925-11-11-11-11 4.925-11 11 4.925 11 11 11ZM16.85 8.56l-3.535 3.535 3.535 3.536-2.121 2.121-3.536-3.536-3.535 3.536-2.122-2.121 3.536-3.536-3.535-3.535 2.12-2.122 3.536 3.536 3.536-3.536 2.121 2.122Z"
                      />
                    </svg>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div
        className={clsx(
          "px-5 bg-athens-gray status-win",
          `coupon-status_${outcomeStatus}`
        )}
      >
        <hr className="text-blue-bayoux border-1 my-0" />
        <div className="coupon-footer">
          {/* TOP LEFT */}
          <div className="tl font-light whitespace-pre">
            {intl.formatMessage({
              id: "boilerplate.components.SportBetsHistoryModal.Bet",
            }) + ": "}
            <span className="font-bold">{formattedBet}</span>
          </div>
          {/* TOP RIGHT */}

          {status === "cashout" ? (
            <OutcomeStatus>
              <div className="font-bold">{formattedWin}</div>
            </OutcomeStatus>
          ) : (
            <>
              {isWin && (
                <OutcomeStatus>
                  {intl.formatMessage({
                    id: "boilerplate.components.SportBetsHistoryModal.Win",
                  }) + ": "}
                  <span className="font-bold">{formattedWin}</span>
                  <WinArrow />
                </OutcomeStatus>
              )}

              {isHalfWin && (
                <OutcomeStatus>
                  {intl.formatMessage({ id: "half_win" }) + ": "}
                  <span className="font-bold">{formattedWin}</span>
                  <WinArrow />
                </OutcomeStatus>
              )}

              {isHalfLost && (
                <OutcomeStatus>
                  {intl.formatMessage({ id: "half_lose" }) + ": "}
                  <span className="font-bold">{formattedWin}</span>
                  <WinArrow />
                </OutcomeStatus>
              )}

              {isDraw && (
                <OutcomeStatus>
                  {"Draw: "}
                  <span className="font-bold">{formattedWin}</span>
                </OutcomeStatus>
              )}

              {isLost && (
                <div className="tr font-bold">
                  {intl.formatMessage({
                    id: "boilerplate.components.SportBetsHistoryModal.Lost",
                  })}
                </div>
              )}
            </>
          )}

          {/* BOTTOM LEFT */}
          <div className="text-[0.625em] bl text-dusty-gray self-end">
            {intl.formatMessage({
              id: "boilerplate.components.SportBetsHistoryModal.Id",
            }) + ": "}{" "}
            {formattedUid}
          </div>

          {BottomRightStatus}
        </div>
      </div>
    </div>
  );
}

function OutcomeStatus({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  return (
    <div className="tr flex items-center justify-end font-light whitespace-pre">
      {children}
    </div>
  );
}

function WinArrow() {
  return (
    <svg
      className="lg:size-3.5 size-3 ml-1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 14 15"
    >
      <path
        className="fill-keppel dark:fill-java"
        d="M14 8.275 7.368.905.734 8.275h3.683v6.628h5.896V8.275h3.688Z"
      />
    </svg>
  );
}
